import { Components, CssVarsTheme, Theme } from '@mui/material';
import type { DataGridComponents } from '@mui/x-data-grid/themeAugmentation';
import { CustomPalette } from '../types';

export default function componentStyles(
  palette: CustomPalette
): Components<Omit<Theme, 'components' | 'palette'> & CssVarsTheme> &
  DataGridComponents<Omit<Theme, 'components' | 'palette'> & CssVarsTheme> {
  return {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: palette.surface['6'],
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        text: {
          color: palette.borders.focus,
        },
        outlined: {
          borderColor: palette.borders.strong,
          borderWidth: '1px',
          borderStyle: 'solid',
        },
      },
    },
    MuiPaper: {
      defaultProps: {
        square: true,
      },
      styleOverrides: {
        root: {
          '--Paper-overlay': 'none',
          backgroundImage: 'none',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: palette.surface['2'],
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          color: palette.text.primary,
          backgroundColor: palette.surface['3'],
          border: `solid 1px ${palette.borders['moderate']}`,
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          background: palette.surface['1'],
          border: `solid 1px ${palette.borders['moderate']}`,
          borderRadius: '8px',
        },
      },
    },
    MuiPopper: {
      styleOverrides: {
        root: {
          ul: {
            background: palette.surface['1'],
            border: `solid 1px ${palette.borders['moderate']}`,
            borderRadius: '8px',
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          height: 3,
          backgroundColor: palette.borders.focus,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none' as const,
          fontWeight: 400,
          fontStyle: 'SemiBold',
          fontSize: '18px',
          leadingTrim: 'none',
          lineHeight: '28px',
          letterSpacing: '0%',
          textAlign: 'center' as const,
          verticalAlign: 'middle',
          color: '#B6C3CC',
          '&.Mui-selected': {
            fontWeight: 600,
            letterSpacing: '1%',
            color: palette.text.primary,
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          '& fieldset': {
            borderColor: palette.borders.strong,
          },
        },
        select: {
          padding: '8px 34px 8px 10px !important',
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        '&::-webkit-scrollbar': {
          width: '5px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: `rgba(255, 255, 255, 0.1)`,
          borderRadius: '10px',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          background: palette.surface['1'],
          color: palette.text.primary,
          borderColor: palette.borders.bold,
          borderWidth: '2px',
          borderStyle: 'solid',
          borderRadius: '0.5rem',
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          borderColor: palette.borders.bold,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          color: palette.text.secondary,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          backgroundColor: palette.surface['2'],
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: palette.borders.bold,
        },
        head: {
          color: palette.text.secondary,
          '&:first-of-type': {
            position: 'relative',

            '&::after': {
              content: '""',
              position: 'absolute',
              top: '50%',
              right: 0,
              width: '1px',
              height: '16px',
              backgroundColor: '#FFFFFF',
              opacity: 0.6,
              transform: 'translateY(-50%)',
            },
          },
        },
        body: {
          backgroundColor: '#1B2A33',
        },
      },
    },
    MuiTableSortLabel: {
      styleOverrides: {
        root: {
          color: palette.text.secondary,
          '&.Mui-active': {
            color: palette.text.secondary,
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          '&.Mui-focused': {
            WebkitTextFillColor: '#ffffff',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '&.Mui-disabled .Mui-disabled': {
            WebkitTextFillColor: 'rgba(255, 255, 255, 0.8)',
          },
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          borderColor: palette.borders.bold,
          border: 0,
          borderRadius: 0,
          overflow: 'auto',
          '--DataGrid-rowBorderColor': 'transparent',
          '& .MuiDataGrid-columnHeaderTitleContainer': {
            paddingTop: '16px',
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 400,
          },
          '& .MuiDataGrid-columnSeparator': {
            color: palette.text.secondary,
            paddingTop: '16px',
            '& .MuiDataGrid-iconSeparator': {
              fontSize: '16px',
            },
          },
        },
        footerContainer: {
          minHeight: '56px', // Ensures consistent footer height, even when the loading overlay is visible.
          backgroundColor: palette.surface['2'],
        },
        row: {
          backgroundColor: palette.surface['2'],
        },
        columnHeader: {
          backgroundColor: palette.surface['5'],
          color: palette.text.secondary,
        },
        columnSeparator: {
          color: palette.text.secondary,
        },
        cell: {
          borderColor: palette.borders.bold,
        },
      },
    },
  };
}
